import { EyeSlash } from '@phosphor-icons/react';
import { useState } from 'react';
import { posts as postsApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/Bits';
import { clearScreenCache, useAsync } from '../../hooks/useAsync';
import { timeAgo } from '../../utils/format';
import { t } from '../../i18n';

/**
 * Les posts masques.
 *
 * Masquer un post etait sans retour : il quittait le fil et plus rien ne
 * permettait de le retrouver. Ils sont ici, tels qu'on les avait vus —
 * l'auteur, le texte, la photo —, avec de quoi les remettre dans le fil.
 */
export function HiddenPostsScreen() {
  const { data, loading, error, reload } = useAsync(() => postsApi.hidden(), [], 'posts-masques');
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  async function afficher(postId: string) {
    setBusy(postId);
    setFailure(null);
    try {
      await postsApi.unhide(postId);
      /* Le fil se relira avec ce post a sa place. */
      clearScreenCache();
      reload();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.profil.masques.impossible);
    } finally {
      setBusy(null);
    }
  }

  return (
    <SubScreen title={t.profil.masques.postsMasques}>
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}
      {failure && <p className="field__error">{failure}</p>}

      {data && data.length === 0 && (
        <EmptyState
          icon={<EyeSlash size={30} />}
          title={t.profil.masques.posts.aucun}
          hint={t.profil.masques.posts.aucunAide}
        />
      )}

      {data && data.length > 0 && (
        <div className="feed__saved-list">
          {data.map((post) => (
            <article key={post.id} className="feed-saved">
              <header className="feed-saved__head">
                <Avatar user={post.author} size={34} />
                <span className="feed-saved__author">{post.author.username}</span>
                <span className="feed-saved__time">· {timeAgo(post.createdAt)}</span>
              </header>
              {post.content && <p className="feed-saved__content">{post.content}</p>}
              {post.imageUrl && (
                <span className="feed-saved__photo">
                  <img src={post.imageUrl} alt={t.fil.commun.photoDe(post.author.username)} />
                </span>
              )}
              <div className="pr-masques__geste">
                <Button
                  variant="soft"
                  size="md"
                  loading={busy === post.id}
                  onClick={() => void afficher(post.id)}
                >
                  {t.profil.masques.afficher}
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}
    </SubScreen>
  );
}
